"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@stocks/ui";
import type { ListProductsQuery, ProductResponse } from "@stocks/shared";

import { usePaginatedList } from "@/shared/libs/query";
import { orderKeys } from "@/entities/order";
import { CreateProductForm } from "@/features/order/create-product";

const PAGE_SIZE = 20;

/** 생산 제품 목록 + 신규 등록 (등록하면 상세에서 BOM 을 이어서 입력) */
export const ProductList = () => {
  const router = useRouter();
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [search, setSearch] = useState("");

  const query: ListProductsQuery = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
  };
  const { data, isLoading, isError } = usePaginatedList<
    ProductResponse,
    ListProductsQuery
  >(orderKeys.products, "/v1/products", query);

  return (
    <div className='flex max-w-5xl flex-col gap-6'>
      <Card>
        <CardHeader>
          <CardTitle>제품 등록</CardTitle>
        </CardHeader>
        <CardContent>
          <CreateProductForm
            onCreated={(p) => router.push(`/orders/products/${p.id}`)}
          />
        </CardContent>
      </Card>

      <div className='flex flex-col gap-4'>
        <form
          className='flex gap-2'
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(keyword.trim());
            setPage(1);
          }}
        >
          <Input
            className='w-64'
            placeholder='제품명 / 코드 검색'
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <Button type='submit'>검색</Button>
        </form>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>코드</TableHead>
              <TableHead>제품명</TableHead>
              <TableHead>설명</TableHead>
              <TableHead>상태</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} className='text-center text-muted-foreground'>
                  불러오는 중…
                </TableCell>
              </TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={4} className='text-center text-destructive'>
                  제품을 불러오지 못했습니다.
                </TableCell>
              </TableRow>
            )}
            {data?.items.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={4} className='text-center text-muted-foreground'>
                  등록된 제품이 없습니다.
                </TableCell>
              </TableRow>
            )}
            {data?.items.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{p.code}</TableCell>
                <TableCell>
                  <Link
                    href={`/orders/products/${p.id}`}
                    className='font-medium hover:underline'
                  >
                    {p.name}
                  </Link>
                </TableCell>
                <TableCell>{p.description ?? "-"}</TableCell>
                <TableCell>
                  <Badge variant={p.isActive ? "default" : "outline"}>
                    {p.isActive ? "사용 중" : "사용 중지"}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {data && (
          <div className='flex items-center justify-end gap-3 text-sm'>
            <span className='text-muted-foreground'>총 {data.meta.total}건</span>
            <Button
              variant='outline'
              size='sm'
              disabled={!data.meta.hasPrev}
              onClick={() => setPage((p) => p - 1)}
            >
              이전
            </Button>
            <span>
              {data.meta.page} / {data.meta.totalPages}
            </span>
            <Button
              variant='outline'
              size='sm'
              disabled={!data.meta.hasNext}
              onClick={() => setPage((p) => p + 1)}
            >
              다음
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
